import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { CircleHelp } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { creditUsageCoverageTooltipContentClasses } from './credit-usage-coverage-tooltip.styles';
import { creditUsagePopoverMessages } from './credit-usage-popover-messages';

const COVERAGE_TOOLTIP_ITEMS = [
  creditUsagePopoverMessages.coverageTooltipMonthlyRefresh,
  creditUsagePopoverMessages.coverageTooltipConsumption,
  creditUsagePopoverMessages.coverageTooltipVariableCost,
] as const;

type CreditUsageCoverageTooltipProps = {
  'data-testid'?: string;
};

/** Circle-help trigger + About Usage tooltip. Same copy as the side-nav popover. */
export function CreditUsageCoverageTooltip({
  'data-testid': testId = 'credit-usage-coverage-tooltip-trigger',
}: CreditUsageCoverageTooltipProps) {
  const intl = useIntl();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            className="size-4 shrink-0 p-0 text-text-secondary"
            aria-label={intl.formatMessage(
              creditUsagePopoverMessages.coverageTooltipAriaLabel
            )}
            data-testid={testId}
          >
            <CircleHelp className="size-4" aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="end"
          sideOffset={12}
          arrowPadding={12}
          className={creditUsageCoverageTooltipContentClasses}
        >
          <div className="flex flex-col">
            <TypographyP2SemiBold className="text-text-primary-on-dark">
              <FormattedMessage {...creditUsagePopoverMessages.coverageTooltipTitle} />
            </TypographyP2SemiBold>
            <ul className="list-disc p-3 ps-4">
              {COVERAGE_TOOLTIP_ITEMS.map((message) => (
                <li key={message.id}>
                  <TypographyCaption className="text-text-secondary-on-dark">
                    <FormattedMessage {...message} />
                  </TypographyCaption>
                </li>
              ))}
            </ul>
          </div>
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
